import * as stylex from '@stylexjs/stylex';
import { useInfiniteQuery, useMutation, useSuspenseInfiniteQuery } from '@tanstack/react-query';
import { createFileRoute, notFound } from '@tanstack/react-router';
import { useMemo } from 'react';

import { ArticleListItem } from '@/components/article-list-item.component';
import { ArticleList } from '@/components/article-list.component';
import { useInfiniteScrollTrigger } from '@/hooks/use-infinite-scroll-trigger.hook';
import {
  Heading,
  ProgressIndicator,
  Text,
  useMediaQuery,
  useMeasure
} from '@/libs/serenity-ui';
import { breakpoints } from '@/libs/serenity-ui/constants.stylex';
import { articleMutations } from '@/resources/article/article.api';
import { publicationQueries } from '@/resources/publication/publication.api';
import { truncateText } from '@/utils/truncate-text.util';

export const Route = createFileRoute('/_app/_list/publishers/$id')({
  component: RouteComponent,
  staticData: {
    contextBarComponent: ContextBarComponent
  },
  loaderDeps: ({ search }) => search,
  loader: async ({ context, params }) => {
    const data = await context.queryClient.ensureInfiniteQueryData(publicationQueries.articles({ publication_id: params.id }));
    if (data?.pages?.[0]?.total === 0) {
      throw notFound({ routeId: '/_app/_list' });
    }
  },
  head: () => ({
    meta: [
      {
        title: 'TrendMD | Publisher',
      },
    ],
  })
});

function RouteComponent() {
  const params = Route.useParams();

  const saveArticleMutation = useMutation(articleMutations.save({ publicationArticles: { publication_id: params.id } }));
  const unsaveArticleMutation = useMutation(articleMutations.unsave({ publicationArticles: { publication_id: params.id } }));
  const excludeArticleMutation = useMutation(articleMutations.exclude({ publicationArticles: { publication_id: params.id } }));
  const undoExcludeArticleMutation = useMutation(articleMutations.removeExclude({ publicationArticles: { publication_id: params.id } }));

  const {
    data,
    fetchNextPage,
    isLoading,
    isFetching,
    hasNextPage
  } = useSuspenseInfiniteQuery(publicationQueries.articles({ publication_id: params.id }));

  useInfiniteScrollTrigger({ isFetching, hasNextPage, fetchNextPage });

  const items = data?.pages?.flatMap((page) => page.articles) ?? [];

  return (
    <>
      <ArticleList aria-label="Publication articles list" items={items}>
        {({ key, index, style, ref, ...item }) => (
          <ArticleListItem
            authors={item.authors}
            data-index={index}
            id={item.id}
            isExcluded={item.excluded}
            isLast={index === items.length - 1}
            isSaved={item.saved}
            isSeen={item.seen}
            key={key}
            publicationDate={item.publication_date}
            publicationId={item.publication_id}
            publicationName={item.publication_name}
            ref={ref}
            style={style}
            title={item.title}
            url={item.url}
            onExcludePress={() => excludeArticleMutation.mutate({ article_id: item.id })}
            onSavePress={() => saveArticleMutation.mutate({ article_id: item.id })}
            onUndoExcludePress={() => undoExcludeArticleMutation.mutate({ article_id: item.id })}
            onUnsavePress={() => unsaveArticleMutation.mutate({ article_id: item.id })}
          />
        )}
      </ArticleList>
      {!isLoading && hasNextPage && <ProgressIndicator size={24} style={pendingIndicatorStyles.base} />}
    </>
  );
}

function ContextBarComponent() {
  const params = Route.useParams();

  const isDesktop = useMediaQuery(breakpoints.large);
  const isMedium = useMediaQuery(breakpoints.medium);

  const { data } = useInfiniteQuery(publicationQueries.articles({ publication_id: params.id }));

  const [ref, dimensions] = useMeasure();
  const isTitleContainerDimensionsMeasured = dimensions.width !== null;

  const publicationName = data?.pages[0]?.publication_name ?? '';

  const containerWidth = isTitleContainerDimensionsMeasured && dimensions.width !== null
    ? dimensions.width - (isDesktop ? 0 : 40)
    : (isDesktop ? 800 : isMedium ? 500 : 250);

  const truncatedText = truncateText(publicationName, {
    maxLines: isDesktop ? 1 : isMedium ? 2 : 3,
    containerWidth,
    fontFamily: 'BasierCircle',
    fontSize: 24,
    lineHeight: 30,
  });

  const shouldCenterTitleContainer = useMemo(() => {
    if (!isTitleContainerDimensionsMeasured || !isDesktop || !publicationName) {
      return false;
    }

    const centeredText = truncateText(publicationName, {
      maxLines: 1,
      containerWidth: 728,
      fontFamily: 'BasierCircle',
      fontSize: 24,
      lineHeight: 30,
    });

    return centeredText.length === publicationName.length;
  }, [isTitleContainerDimensionsMeasured, isDesktop, publicationName]);

  return (
    <Heading level={1} ref={ref} size={24} style={[
      headingStyles.base,
      shouldCenterTitleContainer && headingStyles.centered,
      !isTitleContainerDimensionsMeasured && headingStyles.hidden
    ]}>
      {truncatedText}{' '}
      <Text color="placeholder" size={14}>{data?.pages[0]?.total || ''}</Text>
    </Heading>
  );
}

const pendingIndicatorStyles = stylex.create({
  base: {
    marginBottom: 20,
    marginTop: 56,
  }
});

const headingStyles = stylex.create({
  base: {
    display: 'block',
    paddingBlock: 12,
    paddingInline: 20,
  },
  centered: {
    marginInline: 'auto',
    minWidth: 728,
    paddingInline: 0,
  },
  hidden: {
    minHeight: 54,
    overflow: 'hidden',
    visibility: 'hidden',
  },
});
